import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Zap } from 'lucide-react';
import CountdownTimer from './CountdownTimer';
import SectionHeader from './vitrine/SectionHeader';
import { carregarPromocoesHome } from './vitrine/promocoesHome';
import { MARKETPLACES } from '../design/marketplaces';
import { resolveImageUrl } from '../utils/resolveImageUrl';

/**
 * Central de Promoções e Destaques — os três blocos da vitrine.
 *
 * Uma requisição só, `/api/promotions/home`, alimenta campanhas, destaques por
 * loja e ofertas relâmpago (ver `vitrine/promocoesHome.js`, que compartilha a
 * resposta com o hero).
 *
 * As campanhas ocupam o lugar do HERO da Home: quando existe campanha ativa, o
 * carrossel de banners sai de cena e este bloco vira a primeira coisa da
 * página. Nunca existem dois heros ao mesmo tempo.
 *
 * MONETIZAÇÃO: nenhum link daqui é construído no navegador. O backend entrega
 * rotas internas prontas (`/r/<deal_id>`, `/busca?marketplace=...`), e quem
 * decide o link de afiliado continua sendo o redirect. Montar URL de loja aqui
 * seria a forma mais fácil de perder a atribuição sem ninguém perceber.
 */

const moeda = (v) =>
    typeof v === 'number'
        ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v)
        : null;

// Cor de detalhe da loja, só apresentação. Sem slug conhecido, usa o azul da vitrine.
const corDaLoja = (slug) => (MARKETPLACES.find((m) => m.slug === slug) || {}).cor || 'var(--gold-2)';

const reduzMovimento = () => typeof window !== 'undefined'
    && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ------------------------------------------------------------------ hero */

/**
 * Campanha sem imagem. O backend aceita `banner_image` vazio, então o hero não
 * pode depender dela: sem arte, o lado direito ganha uma composição em CSS com a
 * identidade da vitrine (grade de pontos, anéis em azul vivo e uma etiqueta com a
 * loja), em vez de um bloco vazio. Nenhuma imagem externa, nenhum dado inventado:
 * a etiqueta só mostra a loja quando a campanha tem loja.
 */
function ArteSemImagem({ campanha }) {
    const cor = corDaLoja(campanha.marketplace);
    const mascara = 'linear-gradient(90deg, transparent 0%, #000 45%)';
    return (
        <div aria-hidden="true" className="absolute inset-0 overflow-hidden" data-testid="promo-campaign-arte"
            style={{ background: 'linear-gradient(118deg, #071A3A 0%, #0D2C63 42%, #1552B4 100%)' }}>
            <div className="absolute inset-y-0 right-0 w-[75%]" style={{
                backgroundImage: 'radial-gradient(rgba(169,203,255,0.24) 1.2px, transparent 1.6px)',
                backgroundSize: '22px 22px', maskImage: mascara, WebkitMaskImage: mascara,
            }} />
            <div className="absolute -right-32 -top-40 w-[560px] h-[560px] rounded-full"
                style={{ background: `radial-gradient(circle, ${cor}55 0%, transparent 60%)` }} />
            <div className="absolute -bottom-48 right-[18%] w-[420px] h-[420px] rounded-full"
                style={{ background: 'radial-gradient(circle, rgba(59,234,134,0.16) 0%, transparent 62%)' }} />

            <div className="absolute top-1/2 -translate-y-1/2 -right-40 sm:-right-16 lg:right-[6%] w-[300px] h-[300px] sm:w-[360px] sm:h-[360px] lg:w-[400px] lg:h-[400px] opacity-60 sm:opacity-100">
                <div className="absolute inset-0 rounded-full" style={{ border: '1px solid rgba(125,179,255,0.25)' }} />
                <div className="absolute inset-[16%] rounded-full" style={{ border: '1.5px dashed rgba(125,179,255,0.38)' }} />
                <div className="absolute inset-[32%] rounded-full" style={{
                    background: 'radial-gradient(circle at 35% 30%, #4E8DF0 0%, #1756B8 55%, #0D3270 100%)',
                    boxShadow: '0 0 0 10px rgba(78,141,240,0.12), 0 30px 60px -20px rgba(0,10,40,0.9)',
                }} />
                <span className="absolute top-[12%] left-[20%] w-3 h-3 rounded-full" style={{ background: 'var(--sv-action)', boxShadow: '0 0 18px rgba(59,234,134,0.8)' }} />
                <span className="absolute bottom-[18%] right-[14%] w-2.5 h-2.5 rounded-full" style={{ background: 'var(--sv-discount)' }} />
            </div>

            {campanha.marketplace_name && (
                <div className="hidden sm:flex absolute top-1/2 right-[8%] lg:right-[14%] -translate-y-1/2 rotate-[-8deg] items-center gap-3 pl-4 pr-6 py-4 rounded-2xl"
                    style={{ background: 'var(--sv-media)', boxShadow: '0 24px 48px -18px rgba(0,10,40,0.85)' }}>
                    <span className="w-3.5 h-3.5 rounded-full border-2" style={{ borderColor: '#9FB3D6', background: '#fff' }} />
                    <span className="w-2.5 h-10 rounded-full" style={{ background: cor }} />
                    <span className="text-[20px] lg:text-[24px] font-extrabold whitespace-nowrap"
                        style={{ color: '#071A3A', fontFamily: 'var(--font-display)', letterSpacing: '-0.02em' }}>
                        {campanha.marketplace_name}
                    </span>
                </div>
            )}
        </div>
    );
}

// A área do hero é a referência da dica de imagem no admin (utils/promoForm.js):
// largura total do container, 380 px de altura no desktop, recorte ao centro.
// As alturas são as mesmas do carrossel de banners e do esqueleto do hero: a
// troca entre eles não empurra a página, e no celular cabem selo, título,
// subtítulo, CTA e contador sem cortar texto.
function HeroCampanhas({ campaigns, aoExpirar }) {
    const [atual, setAtual] = useState(0);
    const [pausado, setPausado] = useState(false);
    const total = campaigns.length;

    useEffect(() => {
        if (total <= 1 || pausado || reduzMovimento()) return undefined;
        const id = setInterval(() => setAtual((i) => (i + 1) % total), 7000);
        return () => clearInterval(id);
    }, [total, pausado]);

    useEffect(() => { if (atual >= total) setAtual(0); }, [atual, total]);

    return (
        <section className="pt-5 sm:pt-7" data-testid="promo-campaigns" aria-roledescription="carrossel" aria-label="Campanhas em destaque">
            <div className="site-container mx-auto px-4 sm:px-6 lg:px-8">
                <div
                    className="relative w-full h-[360px] sm:h-[340px] lg:h-[380px] rounded-[22px] overflow-hidden"
                    style={{ background: 'var(--bg-deep)', boxShadow: 'var(--sv-shadow-lift)' }}
                    onMouseEnter={() => setPausado(true)}
                    onMouseLeave={() => setPausado(false)}
                    onFocus={() => setPausado(true)}
                    onBlur={() => setPausado(false)}
                >
                    {campaigns.map((campanha, i) => (
                        <Link
                            key={campanha.campaign_id}
                            to={campanha.target_url}
                            className={`absolute inset-0 transition-opacity duration-500 ${i === atual ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'}`}
                            aria-hidden={i !== atual}
                            tabIndex={i === atual ? 0 : -1}
                            data-testid="promo-campaign-card"
                        >
                            {campanha.banner_image ? (
                                <img
                                    src={resolveImageUrl(campanha.banner_image)}
                                    alt=""
                                    loading={i === 0 ? 'eager' : 'lazy'}
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                            ) : (
                                <ArteSemImagem campanha={campanha} />
                            )}
                            {/* Véu de leitura: forte sobre foto; sem foto, só o bastante
                                para o texto não disputar com os anéis. No celular o texto
                                fica embaixo, então sobre foto o véu sobe de baixo. */}
                            {campanha.banner_image ? (
                                <>
                                    <div aria-hidden="true" className="absolute inset-0 sm:hidden" style={{
                                        background: 'linear-gradient(0deg, rgba(5,20,50,0.95) 0%, rgba(5,20,50,0.7) 50%, rgba(5,20,50,0.1) 100%)',
                                    }} />
                                    <div aria-hidden="true" className="absolute inset-0 hidden sm:block" style={{
                                        background: 'linear-gradient(90deg, rgba(5,20,50,0.92) 0%, rgba(5,20,50,0.62) 45%, rgba(5,20,50,0.05) 80%)',
                                    }} />
                                </>
                            ) : (
                                <div aria-hidden="true" className="absolute inset-0" style={{
                                    background: 'linear-gradient(90deg, rgba(5,20,50,0.55) 0%, rgba(5,20,50,0.2) 40%, transparent 60%)',
                                }} />
                            )}
                            <div className="relative h-full flex flex-col justify-end sm:justify-center gap-3 p-6 sm:p-10 lg:p-14 max-w-[640px]">
                                {campanha.marketplace_name && (
                                    <span className="self-start inline-flex items-center gap-2 text-[12px] font-bold px-2.5 py-1 rounded-full"
                                        style={{ background: 'rgba(255,255,255,0.1)', color: 'var(--text)' }}>
                                        <span aria-hidden="true" className="w-2 h-2 rounded-full" style={{ background: corDaLoja(campanha.marketplace) }} />
                                        {campanha.marketplace_name}
                                    </span>
                                )}
                                <h2 className="text-[26px] sm:text-[36px] lg:text-[46px] font-extrabold leading-[1.04] line-clamp-3"
                                    style={{ color: '#fff', fontFamily: 'var(--font-display)', letterSpacing: '-0.03em' }}>
                                    {campanha.title}
                                </h2>
                                {campanha.subtitle && (
                                    <p className="text-[14px] sm:text-[16px] line-clamp-2" style={{ color: 'rgba(246,248,252,0.86)' }}>
                                        {campanha.subtitle}
                                    </p>
                                )}
                                <div className="flex items-center gap-3 flex-wrap mt-1">
                                    <span className="sv-btn-action">Ver campanha</span>
                                    {/* Só existe contador quando o backend mandou ends_at de verdade. */}
                                    {campanha.show_countdown && campanha.ends_at && (
                                        <CountdownTimer endsAt={campanha.ends_at} prefixo="Termina em" tom="urgente" onExpirar={aoExpirar} />
                                    )}
                                </div>
                            </div>
                        </Link>
                    ))}

                    {total > 1 && (
                        <>
                            <button type="button" onClick={() => setAtual((i) => (i - 1 + total) % total)}
                                className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full items-center justify-center"
                                style={{ background: 'rgba(5,20,50,0.6)', color: '#fff', border: '1px solid var(--border-hover)' }}
                                aria-label="Campanha anterior">
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <button type="button" onClick={() => setAtual((i) => (i + 1) % total)}
                                className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full items-center justify-center"
                                style={{ background: 'rgba(5,20,50,0.6)', color: '#fff', border: '1px solid var(--border-hover)' }}
                                aria-label="Próxima campanha">
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </>
                    )}
                </div>

                {total > 1 && (
                    <div className="flex justify-center gap-2 mt-3" role="tablist" aria-label="Escolher campanha">
                        {campaigns.map((c, i) => (
                            <button key={c.campaign_id} type="button" role="tab" aria-selected={i === atual}
                                aria-label={`Campanha ${i + 1} de ${total}`}
                                onClick={() => setAtual(i)}
                                className="h-1.5 rounded-full transition-all"
                                style={{ width: i === atual ? 28 : 8, background: i === atual ? 'var(--gold-2)' : 'var(--border-hover)' }} />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}

/* ------------------------------------------------------ destaques por loja */

function CardLoja({ loja }) {
    const cor = corDaLoja(loja.marketplace);
    return (
        <Link
            to={loja.url}
            className="sv-card group shrink-0 snap-start w-[240px] lg:w-auto flex flex-col gap-3 p-4 overflow-hidden"
            data-testid="promo-store-card"
        >
            <span aria-hidden="true" className="absolute left-0 right-0 top-0 h-[3px]" style={{ background: cor }} />
            <div className="flex items-center gap-2">
                <span aria-hidden="true" className="w-2.5 h-2.5 rounded-full" style={{ background: cor }} />
                <p className="text-[16px] font-bold truncate" style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}>
                    {loja.name}
                </p>
            </div>
            <p className="sv-num leading-none">
                <span className="text-[30px] font-extrabold" style={{ color: 'var(--text)', fontFamily: 'var(--font-display)', letterSpacing: '-0.03em' }}>
                    {loja.deals_count.toLocaleString('pt-BR')}
                </span>
                <span className="text-[13px] ml-1.5" style={{ color: 'var(--muted)' }}>
                    {loja.deals_count === 1 ? 'oferta' : 'ofertas'}
                </span>
            </p>
            {/* Cada número abaixo só aparece porque o backend confirmou que existe.
                Campo sem dado confiável é omitido, nunca preenchido com estimativa. */}
            <div className="flex flex-wrap gap-1.5">
                {typeof loja.best_discount === 'number' && (
                    <span className="text-[12px] font-bold px-2 py-0.5 rounded-md sv-num"
                        style={{ background: 'rgba(224,45,75,0.16)', color: 'var(--sv-discount)' }}>
                        até -{loja.best_discount}%
                    </span>
                )}
                {loja.coupons_count > 0 && (
                    <span className="text-[12px] px-2 py-0.5 rounded-md" style={{ background: 'var(--surface-2)', color: 'var(--muted)' }}>
                        {loja.coupons_count} cupom{loja.coupons_count > 1 ? 's' : ''}
                    </span>
                )}
                {loja.campaigns_count > 0 && (
                    <span className="text-[12px] px-2 py-0.5 rounded-md" style={{ background: 'var(--surface-2)', color: 'var(--muted)' }}>
                        {loja.campaigns_count} campanha{loja.campaigns_count > 1 ? 's' : ''}
                    </span>
                )}
            </div>
            <span className="sv-card-cta mt-auto">Ver todas</span>
        </Link>
    );
}

/* --------------------------------------------------------- relâmpago */

function CardRelampago({ oferta, aoExpirar }) {
    return (
        <a
            href={oferta.url}
            className="sv-card group shrink-0 snap-start w-[190px] sm:w-[210px] lg:w-auto flex flex-col overflow-hidden"
            style={{ borderColor: 'rgba(255,90,112,0.35)' }}
            data-testid="promo-flash-card"
        >
            <div className="sv-card-media aspect-square flex items-center justify-center">
                {oferta.image_url ? (
                    <img src={resolveImageUrl(oferta.image_url)} alt={oferta.title}
                        loading="lazy" className="w-full h-full object-contain p-3" />
                ) : (
                    <Zap size={24} style={{ color: '#7B8AA6' }} />
                )}
                {typeof oferta.discount_percent === 'number' && oferta.discount_percent > 0 && (
                    <span className="sv-off absolute top-2 left-2 !text-[13px] !px-2 !py-1.5">-{oferta.discount_percent}%</span>
                )}
            </div>
            <div className="flex flex-col gap-2 px-3 pb-3 pt-1 flex-1">
                <p className="text-[13px] font-semibold line-clamp-2 leading-snug min-h-[36px]" style={{ color: 'var(--text)' }}>
                    {oferta.title}
                </p>
                {moeda(oferta.current_price) && (
                    <span className="text-[18px] font-extrabold sv-num" style={{ color: 'var(--text)' }}>
                        {moeda(oferta.current_price)}
                    </span>
                )}
                <CountdownTimer endsAt={oferta.promo_ends_at} prefixo="Termina em" tom="urgente" onExpirar={aoExpirar}
                    className="self-start" />
                <span className="sv-card-cta mt-auto">Ver oferta</span>
            </div>
        </a>
    );
}

/* ------------------------------------------------------------- Central */

// `dadosIniciais`: a resposta que a Home já carregou para decidir o hero. Com
// ela, o primeiro render já desenha a campanha, sem um quadro vazio no meio.
export default function PromotionsHub({ dadosIniciais } = {}) {
    const [dados, setDados] = useState(dadosIniciais === undefined ? null : dadosIniciais);
    const [carregando, setCarregando] = useState(dadosIniciais === undefined);

    const buscar = useCallback((forcar = false) => {
        carregarPromocoesHome({ forcar })
            .then((d) => setDados(d))
            .catch(() => setDados(null))
            .finally(() => setCarregando(false));
    }, []);

    useEffect(() => { if (dadosIniciais === undefined) buscar(); }, [buscar]); // eslint-disable-line react-hooks/exhaustive-deps

    // Quando um contador zera, o item precisa sair da vitrine. Refazer a busca é
    // mais honesto que esconder o card no cliente: quem decide o que ainda está
    // válido é o backend, e a mesma resposta traz o resto atualizado.
    const aoExpirar = useCallback(() => { buscar(true); }, [buscar]);

    if (carregando || !dados) return null;

    const { campaigns = [], stores = [], flash_deals: flash = [] } = dados;
    if (!campaigns.length && !stores.length && !flash.length) return null;

    const trilho = 'sv-rail flex gap-3 sm:gap-4 overflow-x-auto snap-x snap-mandatory -mx-4 px-4 pb-2';

    return (
        <div data-testid="promotions-hub">
            {campaigns.length > 0 && <HeroCampanhas campaigns={campaigns} aoExpirar={aoExpirar} />}

            {flash.length > 0 && (
                <section className="sv-section" data-testid="promo-flash">
                    <div className="site-container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="rounded-[22px] p-4 sm:p-6 lg:p-7" style={{
                            background: 'linear-gradient(160deg, rgba(224,45,75,0.14) 0%, rgba(13,44,99,0.35) 40%, transparent 100%)',
                            border: '1px solid rgba(255,90,112,0.28)',
                        }}>
                            <SectionHeader
                                titulo="Ofertas relâmpago"
                                subtitulo="Preço válido até o contador de cada oferta zerar."
                                acao={{ to: '/busca', label: 'Ver todas' }}
                            />
                            <div className={`${trilho} lg:mx-0 lg:px-0 lg:grid lg:grid-cols-5 lg:overflow-visible`}>
                                {flash.map((o) => (
                                    <CardRelampago key={o.deal_id} oferta={o} aoExpirar={aoExpirar} />
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {stores.length > 0 && (
                <section className="sv-section" data-testid="promo-stores">
                    <div className="site-container mx-auto px-4 sm:px-6 lg:px-8">
                        <SectionHeader
                            titulo="Destaques por loja"
                            subtitulo="Quantas ofertas cada loja tem agora, contando só as que levam direto ao produto."
                        />
                        <div className={`${trilho} lg:mx-0 lg:px-0 lg:grid lg:grid-cols-4 lg:overflow-visible`}>
                            {stores.map((l) => <CardLoja key={l.marketplace} loja={l} />)}
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}
